import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import "./create-lesson.dialog.scoped.scss";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";
import { LinearProgress } from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { icons } from "../../../../statics/icons";
import AppButtonComponent from "../../../../components/form/app-button";
import AppSwitchComponent from "../../../../components/form/app-switch";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { services } from "../../../../services";
import AppRadioComponent from "../../../../components/form/app-radio";
import CloseIcon from "@material-ui/icons/Close";
import useSections from "../../hooks/useSections.hook";

// Constants
const FIELD_LIMITS = {
  TITLE: 80,
  DESCRIPTION: 1200,
  VIDEO_URL: 255,
} as const;

const IMAGE_CONSTRAINTS = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ["image/jpeg", "image/jpg", "image/png"],
  ALLOWED_EXTENSIONS: ["jpg", "jpeg", "png"],
} as const;

// Types
interface CreateLessonDialogProps {
  show: boolean;
  onClose: (value: boolean) => void;
  sectionId?: number;
  dialogClassName?: string;
  lessonData?: any;
  lessonId?: number;
  courseId: number;
}

interface LessonFormData {
  title: string;
  description: string;
  videoUrl: string;
  imageKey: string;
  sectionId: number;
  status: string;
  visibility: string;
  order: number;
  duration: number;
}

interface FormErrors {
  title?: string;
  description?: string;
  videoUrl?: string;
  imageKey?: string;
  sectionId?: string;
  order?: string;
  [key: string]: string | undefined;
}

const CreateLessonDialog: React.FC<CreateLessonDialogProps> = ({
  show,
  onClose,
  sectionId,
  lessonData,
  lessonId,
  courseId,
  // sections,
}) => {
  const { t } = useTranslation();
  const { sections } = useSections(courseId);

  const isEditMode = useMemo(() => Boolean(lessonId != null && lessonData), [
    lessonId,
    lessonData,
  ]);

  // Form state
  const [formData, setFormData] = useState<LessonFormData>({
    title: "",
    description: "",
    videoUrl: "",
    imageKey: "",
    sectionId: sectionId || 0,
    status: "ACTIVE",
    visibility: "PUBLIC",
    duration: 0,
    order: 0,
  });

  // UI state
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Image upload state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined,
  );
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  /**
   * Load lesson into the form when editing
   */
  useEffect(() => {
    if (!isEditMode || !lessonData) return;

    const existingImageKey = lessonData.imageURL || lessonData.imageKey || "";

    setFormData({
      title: lessonData.title || "",
      description: lessonData.description || "",
      videoUrl: lessonData.videoUrl || "",
      imageKey: existingImageKey,
      sectionId: lessonData.sectionId ?? sectionId ?? 0,
      status: lessonData.status ?? "ACTIVE",
      visibility: lessonData.visibility ?? "PUBLIC",
      duration: lessonData.duration ?? 0,
      order:
        lessonData.order !== undefined && lessonData.order !== null
          ? Number(lessonData.order)
          : 0,
    });

    setImageFile(null);
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
    setImagePreview(existingImageKey ? existingImageKey : null);
    setErrors({});
  }, [isEditMode, lessonData, sectionId]);

  /**
   * Get image preview from file
   */
  const getImagePreview = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        resolve(event.target.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Validate image file
   */
  const validateImageFile = useCallback(
    (file: File): string | null => {
      if (!IMAGE_CONSTRAINTS.ALLOWED_TYPES.includes(file.type as any)) {
        return (
          t("EDUCATION_PAGE.INVALID_IMAGE_TYPE") ||
          "Only JPG, JPEG, and PNG files are allowed"
        );
      }

      if (file.size > IMAGE_CONSTRAINTS.MAX_SIZE) {
        const maxSizeMB = IMAGE_CONSTRAINTS.MAX_SIZE / 1024 / 1024;
        return (
          t("EDUCATION_PAGE.IMAGE_TOO_LARGE") ||
          `Image size must be less than ${maxSizeMB}MB`
        );
      }

      return null;
    },
    [t],
  );

  /**
   * Format title to only contain letters and underscores
   */
  const formatTitleForId = useCallback((title: string): string => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^a-z_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
  }, []);

  /**
   * Upload image to signed URL
   */
  const uploadImage = useCallback(
    async (file: File, lessonTitle: string): Promise<string | null> => {
      setUploadingImage(true);
      setUploadProgress(0);

      try {
        const fileExtension =
          file.name.split(".").pop()?.toLowerCase() || "jpg";
        const fileName = `lesson-${Date.now()}.${fileExtension}`;

        const formattedId =
          formatTitleForId(lessonTitle) || `lesson_${Date.now()}`;

        const { signedUrl, url } = await services.lookups.requestSignedUrl(
          fileName,
          "lessons",
          formattedId,
        );

        if (!signedUrl || !url) {
          throw new Error("Failed to get signed URL");
        }

        const uploadResult = await services.lookups.uploadToSignedURL(
          signedUrl,
          file,
          (progressEvent: { loaded: number; total: number }) => {
            const progress = Math.round(
              (100 * progressEvent.loaded) / progressEvent.total,
            );
            setUploadProgress(progress);
          },
        );

        if (uploadResult === 1) {
          return url;
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        showToastMsg(
          "error",
          t("EDUCATION_PAGE.IMAGE_UPLOAD_ERROR") ||
            "Failed to upload image. Please try again.",
        );
        return null;
      } finally {
        setUploadingImage(false);
        setUploadProgress(undefined);
      }
    },
    [t, formatTitleForId],
  );

  /**
   * Handle file input change
   */
  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const error = validateImageFile(file);
      if (error) {
        showToastMsg("error", error);
        if (inputFileRef.current) {
          inputFileRef.current.value = "";
        }
        return;
      }

      setImageFile(file);
      const preview = await getImagePreview(file);
      setImagePreview(preview);

      setFormData((prev) => ({
        ...prev,
        imageKey: "",
      }));

      setErrors((prev) => ({
        ...prev,
        imageKey: "",
      }));
    },
    [validateImageFile, getImagePreview],
  );

  /**
   * Remove image
   */
  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      imageKey: "",
    }));

    const error = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    setErrors((prev) => ({
      ...prev,
      imageKey: error,
    }));

    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  }, [t]);

  /**
   * Trigger file input click
   */
  const handleImageClick = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  /**
   * Validate form field
   */
  const validateField = useCallback(
    (name: keyof LessonFormData, value: any): string => {
      switch (name) {
        case "title":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.VIDEO_NAME_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.TITLE) {
            return `Title must be less than ${FIELD_LIMITS.TITLE} characters`;
          }
          return "";
        case "description":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.VIDEO_DESCRIPTION_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.DESCRIPTION) {
            return `Description must be less than ${FIELD_LIMITS.DESCRIPTION} characters`;
          }
          return "";
        case "videoUrl":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.VIDEO_URL_REQUIRED");
          }
          if (value.length > FIELD_LIMITS.VIDEO_URL) {
            return `Video URL must be less than ${FIELD_LIMITS.VIDEO_URL} characters`;
          }
          // Basic URL validation
          try {
            new URL(value);
          } catch {
            return t("EDUCATION_PAGE.INVALID_URL");
          }
          return "";
        case "imageKey":
          if (!value || value.trim() === "") {
            return t("EDUCATION_PAGE.IMAGE_REQUIRED");
          }
          return "";
        case "order":
          if (value === null || value === undefined || value < 0) {
            return t("EDUCATION_PAGE.ORDER_REQUIRED");
          }
          return "";
        default:
          return "";
      }
    },
    [t],
  );

  /**
   * Handle text input changes
   */
  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | { name?: string; value: unknown }
      >,
    ) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof LessonFormData;

      const processedValue =
        fieldName === "order"
          ? String(value) === "0"
            ? "0"
            : String(Number(value))
          : typeof value === "string"
          ? value
          : String(value);

      setFormData((prev) => ({ ...prev, [fieldName]: processedValue }));
      const error = validateField(fieldName, processedValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  useEffect(() => {
    if (!formData.videoUrl?.trim()) return;
    let cancelled = false;
    services.courses
      .getLessonDuration(formData.videoUrl as string)
      .then((duration) => {
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, duration }));
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [formData.videoUrl]);

  /**
   * Handle select dropdown changes
   * Select component has different onChange signature: (event, value)
   */
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof LessonFormData;
      const numValue = typeof value === "number" ? value : Number(value);

      setFormData((prev) => ({ ...prev, [fieldName]: numValue }));

      // Validate field
      const error = validateField(fieldName, numValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  /**
   * Check if form is valid
   */
  const isFormValid = useMemo(() => {
    const hasErrors = Object.values(errors).some((error) => error !== "");

    const imageProvided =
      imageFile !== null ||
      (formData.imageKey && formData.imageKey.trim() !== "");

    const requiredFieldsValid =
      formData.title.trim() !== "" &&
      formData.description.trim() !== "" &&
      formData.videoUrl.trim() !== "" &&
      formData.order >= 0 &&
      imageProvided;

    return !hasErrors && requiredFieldsValid;
  }, [formData, errors, imageFile]);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      description: "",
      videoUrl: "",
      imageKey: "",
      sectionId: sectionId || 0,
      status: "ACTIVE",
      visibility: "PUBLIC",
      order: 0,
      duration: 0,
    });
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(undefined);
    setErrors({});
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  }, [sectionId]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(
    (success: boolean = false) => {
      if (!saving) {
        resetForm();
        onClose(success);
      }
    },
    [saving, resetForm, onClose],
  );

  /**
   * Submit form
   */
  const handleSubmit = useCallback(async () => {
    const finalErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof LessonFormData;
      if (fieldName === "imageKey" && imageFile) {
        return;
      }
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        finalErrors[fieldName] = error;
      }
    });

    if (!isEditMode && !imageFile && !formData.imageKey) {
      finalErrors.imageKey = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    } else if (isEditMode && !imageFile && !formData.imageKey) {
      finalErrors.imageKey = t("EDUCATION_PAGE.IMAGE_REQUIRED");
    }

    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      showToastMsg("error", t("EDUCATION_PAGE.PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    setSaving(true);
    let shouldClose = false;
    try {
      let imageKey: string | undefined = formData.imageKey || undefined;

      if (imageFile) {
        const uploadedImageKey = await uploadImage(imageFile, formData.title);
        if (uploadedImageKey) {
          imageKey = uploadedImageKey;
        } else {
          setSaving(false);
          return;
        }
      }

      const submitData: Record<string, unknown> = {
        title: formData.title,
        description: formData.description,
        videoUrl: formData.videoUrl,
        sectionId: formData.sectionId,
        status: formData.status,
        visibility: formData.visibility,
        order: formData.order,
        duration: formData.duration,
      };

      if (!isEditMode) {
        submitData.imageKey = imageKey || "";
      } else if (imageFile && imageKey) {
        submitData.imageKey = imageKey;
      }

      let response;
      if (isEditMode && lessonId != null) {
        response = await services.courses.updateLesson(
          String(lessonId),
          submitData,
        );
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.LESSON_UPDATED_SUCCESSFULLY"),
          );
          shouldClose = true;
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      } else {
        response = await services.courses.addLesson(submitData);
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.LESSON_CREATED_SUCCESSFULLY"),
          );
          shouldClose = true;
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} lesson:`,
        error,
      );
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
    if (shouldClose) {
      handleClose(true);
    }
  }, [
    formData,
    validateField,
    t,
    handleClose,
    imageFile,
    uploadImage,
    isEditMode,
    lessonId,
  ]);

  return (
    <Dialog
      open={show}
      onClose={() => !saving && handleClose(false)}
      maxWidth="md"
      fullWidth
      disableBackdropClick={saving}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <DialogTitle
        style={{
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        disableTypography
      >
        <Typography variant="h2" className="m-0">
          {isEditMode
            ? t("EDUCATION_PAGE.EDIT_VIDEO")
            : t("EDUCATION_PAGE.ADD_VIDEO")}
        </Typography>

        <IconButton
          aria-label={t("CLOSE")}
          edge="end"
          onClick={() => handleClose(false)}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className="create__lesson__form">
          <Box className="lesson__section">
            <Typography
              variant="h3"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("EDUCATION_PAGE.VIDEO_DETAILS")}
            </Typography>
          </Box>

          {/* Title */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.VIDEO_NAME")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.VIDEO_NAME")}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.TITLE,
                }}
              />
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.title) && (
                  <Typography variant="caption" color="error">
                    {errors.title}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  className="px-2 text-right"
                  style={{ flex: "auto" }}
                  color="textPrimary"
                >
                  {formData.title.length}/{FIELD_LIMITS.TITLE}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Video URL */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.VIDEO_LINK")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.VIDEO_LINK")}
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.VIDEO_URL,
                }}
              />
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.videoUrl) && (
                  <Typography variant="caption" color="error">
                    {errors.videoUrl}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Video Services */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.VIDEO_SERVICES")}*
            </Typography>
            <AppRadioComponent
              onChange={() => {}}
              name="videoServices"
              value="true"
              options={[
                {
                  value: "true",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>Vimeo</strong>
                    </Typography>
                  ),
                },
                {
                  value: "false",
                  disabled: true,
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>PeerTube</strong>
                    </Typography>
                  ),
                },
              ]}
            />
          </Box>

          {/* Image Upload Section */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.THUMBNAIL")}*
            </Typography>
            <Box className="standard_text_input">
              <input
                ref={inputFileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />

              {imagePreview ? (
                <Box
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "14 / 11",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Lesson preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid var(--border)",
                    }}
                  />

                  <Box className="remove-image" onClick={handleRemoveImage}>
                    <img src={icons.close} alt="" width={16} height={16} />
                  </Box>
                  {uploadingImage && uploadProgress !== undefined && (
                    <Box style={{ marginTop: "8px" }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                      />
                      <Typography
                        variant="caption"
                        style={{ marginTop: "4px" }}
                      >
                        {uploadProgress}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <AppButtonComponent
                  variant="outlined"
                  color="secondary"
                  onClick={handleImageClick}
                  disabled={uploadingImage}
                  startIcon={<CloudUploadIcon />}
                  size="large"
                  style={{
                    width: "100%",
                    padding: "12px",
                    textTransform: "none",
                  }}
                >
                  {uploadingImage
                    ? t("EDUCATION_PAGE.UPLOADING")
                    : t("EDUCATION_PAGE.UPLOAD_IMAGE")}
                </AppButtonComponent>
              )}

              {formData.imageKey && !imagePreview && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: "8px" }}
                >
                  {t("EDUCATION_PAGE.IMAGE_UPLOADED")}
                </Typography>
              )}
              <Box className="hint d-flex align-items-center justify-content-between">
                <Typography
                  variant="body2"
                  className="px-2"
                  color="textPrimary"
                >
                  280x220px JPEG, PNG Image
                </Typography>
              </Box>
              {Boolean(errors.imageKey) && (
                <Typography
                  variant="caption"
                  color="error"
                  className="px-2"
                  style={{ marginTop: "4px" }}
                >
                  {errors.imageKey}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Description */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.VIDEO_DESCRIPTION")}*
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                placeholder={t("EDUCATION_PAGE.VIDEO_DESCRIPTION")}
                multiline
                name="description"
                rows={8}
                value={formData.description}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: FIELD_LIMITS.DESCRIPTION,
                }}
              />
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.description) && (
                  <Typography variant="caption" color="error">
                    {errors.description}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  className="px-2 text-right"
                  color="textPrimary"
                  style={{ flex: "auto" }}
                >
                  {formData.description.length}/{FIELD_LIMITS.DESCRIPTION}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Order */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.ORDER")}
            </Typography>
            <Box className="standard_text_input">
              <TextField
                className="w-100"
                variant="standard"
                type="number"
                placeholder={t("EDUCATION_PAGE.ORDER")}
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                inputProps={{
                  min: 0,
                }}
              />
              <Box className="hint d-flex align-items-center justify-content-between">
                {Boolean(errors.order) && (
                  <Typography variant="caption" color="error">
                    {errors.order}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" className="mt-3" color="textPrimary">
                {t("EDUCATION_PAGE.ORDER_SECTION_HINT")}
              </Typography>
            </Box>
          </Box>

          <Box className="grid-2 mb-3">
            <Typography variant="body1">
              {t("EDUCATION_PAGE.SECTION")}
            </Typography>
            <Box className="standard_text_input dropdown">
              <FormControl className="select-input select-grey w-100">
                <Select
                  name="sectionId"
                  onChange={handleSelectChange}
                  IconComponent={ExpandMoreIcon}
                  value={formData.sectionId}
                  error={Boolean(errors.sectionId)}
                >
                  <MenuItem value={0} disabled>
                    {t("EDUCATION_PAGE.SELECT_SECTION")}
                  </MenuItem>
                  {sections.map((value: any, index: number) => (
                    <MenuItem key={index} value={value.id}>
                      {t(value.title)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Status */}
          <Box className="lesson__section">
            <Typography
              variant="h3"
              className="font-weight-bold"
              color="textPrimary"
            >
              {t("EDUCATION_PAGE.ASSIGNMENT_AND_VISIBILITY")}
            </Typography>
          </Box>

          {/* Status */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("STATUS")}
            </Typography>
            <Box className="standard_text_input">
              <AppSwitchComponent
                label={t("ACTIVE")}
                onChange={(e: any) => {
                  const { checked } = e.target;
                  setFormData((prev) => ({
                    ...prev,
                    status: checked ? "ACTIVE" : "INACTIVE",
                  }));
                }}
                checked={formData.status === "ACTIVE"}
                name="status"
                size="small"
                color="primary"
              />
              <Typography variant="body2" className="mt-3" color="textPrimary">
                {t("EDUCATION_PAGE.ENABLES_OR_DISABLES_THE_VIDEO")}
              </Typography>
            </Box>
          </Box>

          {/* Visibility Section */}
          <Box className="lesson__section">
            <Typography
              variant="h3"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("EDUCATION_PAGE.ACCESS_AND_VISIBILITY")}
            </Typography>
          </Box>

          {/* Public */}
          <Box className="grid-2 mb-3">
            <Typography variant="body1" color="textPrimary">
              {t("EDUCATION_PAGE.WHO_CAN_SEE_THIS_VIDEO")}
            </Typography>
            <AppRadioComponent
              name="visibility"
              value={formData.visibility}
              onChange={(value: string) =>
                setFormData((prev: LessonFormData) => ({
                  ...prev,
                  visibility: value,
                }))
              }
              options={[
                {
                  value: "PUBLIC",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.ALL_COURSE_MEMBERS")}</strong>
                    </Typography>
                  ),
                },
                {
                  value: "ADMIN_ONLY",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.ONLY_ADMINS")}</strong>
                    </Typography>
                  ),
                },
              ]}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        style={{
          borderTop: "1px solid var(--border)",
          padding: "16px 24px",
          gap: "1rem",
        }}
      >
        <AppButtonComponent
          variant="text"
          color="secondary"
          disabled={saving}
          onClick={() => handleClose(false)}
        >
          {t("CANCEL")}
        </AppButtonComponent>
        <AppButtonComponent
          variant="contained"
          color="primary"
          disabled={!isFormValid || saving}
          onClick={handleSubmit}
        >
          {isEditMode ? t("SAVE") : t("CREATE")}
        </AppButtonComponent>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLessonDialog;
